/**
 * Read an element's inline styles and create a function that
 * re-applies exactly those snapshotted styles.
 */
export function captureInlineStyles(element: HTMLElement) {
  const styles = [...element.style].map(property => ({
    property,
    value: element.style.getPropertyValue(property),
    priority: element.style.getPropertyPriority(property),
  }))

  return () => {
    element.removeAttribute('style')
    for (const { property, value, priority } of styles) {
      element.style.setProperty(property, value, priority)
    }
  }
}

const implicitQueueKeys = new WeakMap<HTMLElement, symbol>()
const queues = new Map<string | symbol, Array<() => Promise<void>>>()

export type AnimateOptions<T extends HTMLElement> = {
  /**
   * Called when the Web Animations API is not supported
   */
  onUnsupported?: (element: T) => void

  /**
   * Called before original styles of the element are captured
   */
  onCapture?: (element: T) => void

  /**
   * Called before the animation starts
   */
  onBeforeAnimate?: (element: T) => void

  /**
   * Called after the animation starts
   */
  onAnimate?: (element: T, animation: Animation) => void

  /**
   * Called when the animation is canceled
   */
  onAbort?: (element: T) => void

  /**
   * Called when the animation finishes successfully
   */
  onFinish?: (element: T) => void

  /**
   * Called after the animation either finishes or is canceled
   */
  onComplete?: (element: T) => void

  /**
   * A queue name (string or symbol) to push animations into or false if
   * existing animations should be canceled instead of awaited.
   * By default, an individual queue for each animated element is used.
   */
  queue?: string | symbol | false

  /**
   * If enabled, the animation will be performed starting from the provided CSS properties
   * towards the pre-existing state of the element.
   */
  reverse?: boolean

  /**
   * If enabled, inline styles applied through the animation will be kept
   * after the animation finishes.
   */
  persist?: boolean

  /**
   * Animation duration in milliseconds
   */
  duration?: number

  /**
   * Animation easing function name
   */
  easing?: string

  /**
   * An AbortSignal to cancel the animation
   */
  signal?: AbortSignal

  /**
   * What to do when the animation is aborted
   * - 'cancel' to pause the animation in its current state and
   *   apply the current state as inline styles
   * - 'finish' to instantly go to the target style
   */
  abortAction?: 'cancel' | 'finish'

  /**
   * Whether the animation is queued. Only for internal use.
   */
  queued?: boolean
}

/**
 * Animate an element's CSS properties
 *
 * @param element The DOM element to animate
 * @param values The CSS properties to animate to
 * @param options Animation options
 */
async function animate<T extends HTMLElement>(
  element: T,
  values: Record<string, any>,
  options: AnimateOptions<T> = {},
): Promise<void> {
  const {
    // Callbacks
    onCapture,
    onBeforeAnimate,
    onAnimate,
    onAbort,
    onFinish,
    onComplete,
    onUnsupported,

    // Options
    queued: discardWhenQueued = false,
    reverse = false,
    persist = false,
    duration = 500,
    easing = 'ease',
    signal,
    abortAction = 'finish',
  } = options
  let { queue } = options

  const supported = typeof element.animate === 'function'
  const shouldCancel = signal?.aborted

  if (shouldCancel || !supported) {
    if (persist && !reverse) {
      Object.assign(element.style, values)
    }

    if (!supported) {
      onUnsupported?.(element)
    }

    return
  }

  if (typeof queue === 'undefined') {
    if (implicitQueueKeys.has(element)) {
      queue = implicitQueueKeys.get(element)
    } else {
      queue = Symbol('animation queue')
      implicitQueueKeys.set(element, queue)
    }
  }

  if (queue !== false) {
    if (typeof queue !== 'string' && typeof queue !== 'symbol') {
      throw new Error('Invalid "queue" option')
    }

    if (!queues.has(queue)) {
      queues.set(queue, [])
    }

    if (!discardWhenQueued && queues.get(queue)!.length !== 0) {
      return new Promise(resolve => {
        queues
          .get(queue as string | symbol)!
          .push(() => animate(element, values, { ...options, queued: true })?.then(resolve))
      })
    } else {
      // We need to unshift anything to the queue to make future animate()
      // calls queue up correctly
      queues.get(queue)!.unshift(null as any)
    }
  }

  onCapture?.(element)
  const restoreInlineStyles = captureInlineStyles(element)

  const properties = Object.keys(values)
  const computedStyle = getComputedStyle(element)

  const startStyle = Object.fromEntries(
    properties.map(property => [property, computedStyle[property as keyof CSSStyleDeclaration]]),
  ) as Keyframe

  Object.assign(element.style, values)

  const computedStyle2 = getComputedStyle(element)
  const endStyle = Object.fromEntries(
    properties.map(property => [property, computedStyle2[property as keyof CSSStyleDeclaration]]),
  ) as Keyframe

  restoreInlineStyles()

  onBeforeAnimate?.(element)

  const animation = element.animate(reverse ? [endStyle, startStyle] : [startStyle, endStyle], {
    duration,
    easing,
  })

  onAnimate?.(element, animation)

  if (signal) {
    const listener = () => {
      if (abortAction === 'cancel') {
        animation.pause()
        const styles = Object.fromEntries(
          properties.map(property => [
            property,
            computedStyle[property as keyof CSSStyleDeclaration],
          ]),
        )
        animation.cancel()
        Object.assign(element.style, styles)
      } else {
        animation.finish()
      }

      onAbort?.(element)
    }

    signal.addEventListener('abort', listener)
    animation.finished
      .catch(() => {})
      .then(() => {
        signal!.removeEventListener('abort', listener)
      })
  }

  await animation.finished
    .then(() => {
      onFinish?.(element)
    })
    .catch(() => {})

  onComplete?.(element)

  if (persist && !reverse && abortAction !== 'cancel') {
    Object.assign(element.style, values)
  }

  if (queue !== false) {
    queues.get(queue)!.shift()

    if (queues.get(queue)!.length > 0) {
      const next = queues.get(queue)!.shift()!
      next()
    } else {
      queues.delete(queue)
    }
  }
}

/**
 * Animate an element from the target state
 *
 * @param element The DOM element to animate
 * @param values The CSS properties to animate to
 * @param options Animation options
 */
export function from<T extends HTMLElement>(
  element: T,
  values: Record<string, any>,
  options: Omit<AnimateOptions<T>, 'queued'> = {},
) {
  return animate(element, values, {
    ...options,
    reverse: true,
  })
}

/**
 * Animate an element to the target state
 *
 * @param element The DOM element to animate
 * @param values The CSS properties to animate to
 * @param options Animation options
 */
export function to<T extends HTMLElement>(
  element: T,
  values: Record<string, any>,
  options: Omit<AnimateOptions<T>, 'queued'> = {},
) {
  return animate(element, values, {
    ...options,
    reverse: false,
  })
}

export type SlideUpOptions = {
  /**
   * Animation duration in milliseconds
   */
  duration?: number

  /**
   * Animation easing function name
   */
  easing?: string

  /**
   * A queue name (string or symbol) to push animations into or false if
   * existing animations should be canceled instead of awaited.
   * By default, an individual queue for each animated element is used.
   */
  queue?: string | symbol | false
}

/**
 * Slide up to hide an element
 *
 * @param element The DOM element to slide up
 * @param options Animation options
 */
export async function slideUp<T extends HTMLElement>(
  element: T,
  { duration = 600, easing = 'ease', queue }: SlideUpOptions = {},
) {
  const computedStyle = getComputedStyle(element)
  if (computedStyle.display === 'none') return

  await to(
    element,
    {
      height: '0',
      paddingTop: '0',
      paddingBottom: '0',
      marginTop: '0',
      marginBottom: '0',
      borderTopWidth: '0',
      borderBottomWidth: '0',
    },
    {
      duration,
      easing,
      queue,
      onBeforeAnimate() {
        element.style.overflow = 'hidden'
      },
      onComplete() {
        element.style.removeProperty('overflow')
        element.style.display = 'none'
      },
      onUnsupported() {
        element.style.display = 'none'
      },
    },
  )
}

export type SlideDownOptions = SlideUpOptions & {
  /**
   * What display mode to use when the element is shown
   */
  display?: string
}

/**
 * Slide down a hidden element
 *
 * @param element The DOM element to slide down
 * @param options Animation options
 */
export async function slideDown<T extends HTMLElement>(
  element: T,
  { duration = 600, display = 'block', easing = 'ease', queue }: SlideDownOptions = {},
) {
  await from(
    element,
    {
      height: '0',
      paddingTop: '0',
      paddingBottom: '0',
      marginTop: '0',
      marginBottom: '0',
      borderTopWidth: '0',
      borderBottomWidth: '0',
    },
    {
      duration,
      easing,
      queue,
      onCapture() {
        element.style.display = display
      },
      onBeforeAnimate() {
        element.style.overflow = 'hidden'
      },
      onComplete() {
        element.style.removeProperty('overflow')
      },
      onUnsupported() {
        element.style.display = display
      },
    },
  )
}
